import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Description, Label, ListBox, Select } from '@heroui/react';
import type {
    ChartAccount,
    OperationLineDraft,
    OperationLineSide,
    OperationPairRole,
} from '@/api/models';
import type { AccountLabel } from '@/pages/accounting/books/journal/components/journalEntryLines';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    AMOUNT_SOURCES,
    type DraftPair,
    type OpenLine,
} from '../../operationEditorMeta';
import { duplicateSlotPairs, slotsResolvedAtEntry } from '../../pairBoxMeta';
import { LineBox } from './LineBox';
import { PairArrow } from './PairArrow';
import { PairRoleChip } from './PairRoleChip';
import { Widget } from '@heroui-pro/react';

type PairEditorProps = {
    pairs: DraftPair[];
    accounts: ChartAccount[];
    /** The whole chart — group headers and leaves — what a slot's filter picker offers. */
    chartAccounts: ChartAccount[];
    /** Account id → code + localized name, both languages, for the slot label auto-suggestion. */
    accountLabels: {
        ka: Map<string, AccountLabel>;
        en: Map<string, AccountLabel>;
    };
    /** A BranchAdmin reads the recipe; every control is disabled for them. */
    isReadOnly: boolean;
    /** How many pairs the version in force has — decides "remove" vs "drop". */
    publishedPairCount: number;
    /** The version a publish would create — `Drop from v4`. */
    nextVersion: number;
    /** The tenant's VAT rate in basis points, for the `18/118` badge. */
    vatRateBps: number;
    /** The one box currently open for editing, across the whole recipe. */
    openLine: OpenLine | null;
    onOpenLine: (line: OpenLine | null) => void;
    onLineChange: (
        pairGroup: number,
        side: OperationLineSide,
        patch: Partial<OperationLineDraft>,
    ) => void;
    onLineKindChange: (
        pairGroup: number,
        side: OperationLineSide,
        kind: 'fixed' | 'slot',
    ) => void;
    onPairRoleChange: (pairGroup: number, role: OperationPairRole) => void;
    onAddPair: () => void;
    onRequestDropPair: (pairGroup: number) => void;
};

const CAPTION = 'text-[10px] font-bold uppercase tracking-widest text-muted';

/**
 * The recipe, pair by pair.
 *
 * **The pair is the unit, not the line**, and the card says so: a debit box, an
 * arrow, a credit box, with the role the pair plays above them and the amount
 * they both take below. A flat list of lines with a "pair" column would let an
 * author build a debit with no credit without noticing, which is the mistake
 * this feature can least afford.
 *
 * The arrow between the boxes is load-bearing — see `PairArrow`.
 *
 * The amount source sits on the **pair** because the server requires both sides
 * to share it: a pair whose sides take different figures cannot balance. Making
 * it one control rather than two is what stops the author building something
 * that will be refused.
 *
 * A `VAT_OF_GROSS` pair carries a locked `18 / 118` badge beside its source.
 * The **fraction** is what is locked, not the choice: which figure a pair takes
 * is the author's call, but what "the VAT inside it" works out to is the
 * tenant's rate set in force, applied at record time against the entry's own
 * date. Locking the dropdown itself would trap anyone who picked VAT by
 * mistake.
 */
export const PairEditor: React.FC<PairEditorProps> = ({
    pairs,
    accounts,
    chartAccounts,
    accountLabels,
    isReadOnly,
    publishedPairCount,
    nextVersion,
    vatRateBps,
    openLine,
    onOpenLine,
    onLineChange,
    onLineKindChange,
    onPairRoleChange,
    onAddPair,
    onRequestDropPair,
}) => {
    const { t } = useTranslation('pages');
    const slots = slotsResolvedAtEntry(pairs);

    return (
        <Widget
            className="flex flex-col gap-3"
            {...getNodeTestIdProp('operation-editor-pairs')}
        >
            <Widget.Header className="my-4">
                <Widget.Title>
                    {t('operations.editor.pairsHeading')}
                </Widget.Title>
                <Widget.Description>
                    {slots.length > 0 ? (
                        <span
                            className="text-xs text-muted"
                            {...getNodeTestIdProp(
                                'operation-editor-slot-summary',
                            )}
                        >
                            {t('operations.editor.slotsResolved')}{' '}
                            <strong className="font-medium text-foreground">
                                {slots.join(', ')}
                            </strong>
                        </span>
                    ) : null}
                </Widget.Description>
            </Widget.Header>
            <Widget.Content className="flex flex-col gap-3 p-0 bg-transparent">
                {pairs.map((pair, index) => {
                    const duplicates = duplicateSlotPairs(
                        pairs,
                        pair.pairGroup,
                    );
                    const isVat = pair.debit.amountSource === 'VAT_OF_GROSS';
                    return (
                        <div
                            className="flex flex-col gap-3 rounded-2xl border border-separator bg-surface p-4 shadow-xs"
                            key={pair.pairGroup}
                            {...getNodeTestIdProp(
                                `operation-editor-pair-${pair.pairGroup}`,
                            )}
                        >
                            <div className="flex flex-wrap items-center gap-2">
                                <span className={CAPTION}>
                                    {t('operations.editor.pairLabel', {
                                        number: index + 1,
                                    })}
                                </span>
                                <PairRoleChip
                                    isReadOnly={isReadOnly}
                                    onChange={(role) =>
                                        onPairRoleChange(pair.pairGroup, role)
                                    }
                                    pairGroup={pair.pairGroup}
                                    role={pair.role}
                                />
                                {isReadOnly ? null : (
                                    <Button
                                        className="ms-auto"
                                        onPress={() =>
                                            onRequestDropPair(pair.pairGroup)
                                        }
                                        size="sm"
                                        variant="outline"
                                        {...getNodeTestIdProp(
                                            `operation-editor-remove-pair-${pair.pairGroup}`,
                                        )}
                                    >
                                        {t(
                                            index < publishedPairCount
                                                ? 'operations.editor.dropFrom'
                                                : 'operations.editor.removePair',
                                            { next: nextVersion },
                                        )}
                                    </Button>
                                )}
                            </div>

                            <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-[1fr_24px_1fr]">
                                <LineBox
                                    accountLabels={accountLabels}
                                    accounts={accounts}
                                    chartAccounts={chartAccounts}
                                    isOpen={
                                        openLine?.pairGroup ===
                                            pair.pairGroup &&
                                        openLine.side === 'DEBIT'
                                    }
                                    isReadOnly={isReadOnly}
                                    line={pair.debit}
                                    onClose={() => onOpenLine(null)}
                                    onLineChange={(patch) =>
                                        onLineChange(
                                            pair.pairGroup,
                                            'DEBIT',
                                            patch,
                                        )
                                    }
                                    onLineKindChange={(kind) =>
                                        onLineKindChange(
                                            pair.pairGroup,
                                            'DEBIT',
                                            kind,
                                        )
                                    }
                                    onOpen={() =>
                                        onOpenLine({
                                            pairGroup: pair.pairGroup,
                                            side: 'DEBIT',
                                        })
                                    }
                                    pairGroup={pair.pairGroup}
                                    side="DEBIT"
                                />

                                <PairArrow />

                                <LineBox
                                    accountLabels={accountLabels}
                                    accounts={accounts}
                                    chartAccounts={chartAccounts}
                                    isOpen={
                                        openLine?.pairGroup ===
                                            pair.pairGroup &&
                                        openLine.side === 'CREDIT'
                                    }
                                    isReadOnly={isReadOnly}
                                    line={pair.credit}
                                    onClose={() => onOpenLine(null)}
                                    onLineChange={(patch) =>
                                        onLineChange(
                                            pair.pairGroup,
                                            'CREDIT',
                                            patch,
                                        )
                                    }
                                    onLineKindChange={(kind) =>
                                        onLineKindChange(
                                            pair.pairGroup,
                                            'CREDIT',
                                            kind,
                                        )
                                    }
                                    onOpen={() =>
                                        onOpenLine({
                                            pairGroup: pair.pairGroup,
                                            side: 'CREDIT',
                                        })
                                    }
                                    pairGroup={pair.pairGroup}
                                    side="CREDIT"
                                />
                            </div>

                            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                                <Select
                                    className="w-fit flex-row items-center gap-2"
                                    isDisabled={isReadOnly}
                                    onChange={(key) =>
                                        key &&
                                        onLineChange(pair.pairGroup, 'DEBIT', {
                                            amountSource:
                                                key as OperationLineDraft['amountSource'],
                                        })
                                    }
                                    value={pair.debit.amountSource}
                                >
                                    <Label className="text-xs text-muted">
                                        {t(
                                            'operations.editor.amountSourceLabel',
                                        )}
                                    </Label>
                                    {/* The chip's height and type are ours; the
                                    inline padding is the kit's, because that is
                                    what reserves the lane the indicator is
                                    absolutely positioned into. Override it and
                                    the chevron lands on the last letter. */}
                                    <Select.Trigger
                                        className="min-h-0 rounded-xl py-1.5 text-xs font-semibold"
                                        {...getNodeTestIdProp(
                                            `operation-editor-source-${pair.pairGroup}`,
                                        )}
                                    >
                                        {/* Label only. The hint explains the
                                        choice while it is being made; repeated
                                        in the closed trigger it turns a chip
                                        into a paragraph. */}
                                        <Select.Value>
                                            {({ selectedText }) => selectedText}
                                        </Select.Value>
                                        <Select.Indicator />
                                    </Select.Trigger>
                                    <Select.Popover>
                                        <ListBox>
                                            {AMOUNT_SOURCES.map((source) => (
                                                <ListBox.Item
                                                    id={source.id}
                                                    key={source.id}
                                                    textValue={t(
                                                        source.labelKey,
                                                    )}
                                                >
                                                    <div className="flex flex-col">
                                                        <Label>
                                                            {t(source.labelKey)}
                                                        </Label>
                                                        <Description>
                                                            {t(source.hintKey)}
                                                        </Description>
                                                    </div>
                                                    <ListBox.ItemIndicator />
                                                </ListBox.Item>
                                            ))}
                                        </ListBox>
                                    </Select.Popover>
                                </Select>

                                {/* The *fraction* is locked, not the choice.
                                Which figure this pair takes is the author's
                                call; what 'the VAT inside it' works out to is
                                the tenant's rate set in force, applied at
                                record time against the entry's own date. A
                                padlock on the dropdown would trap an author who
                                picked VAT by mistake. */}
                                {isVat ? (
                                    <span
                                        className="inline-flex items-center gap-1.5 rounded-xl border border-separator bg-surface-secondary px-2.5 py-1.5 text-xs font-semibold tabular-nums text-muted"
                                        {...getNodeTestIdProp(
                                            `operation-editor-vat-fraction-${pair.pairGroup}`,
                                        )}
                                    >
                                        <Icon
                                            aria-hidden
                                            className="size-3.5"
                                            icon="gravity-ui:lock"
                                        />
                                        {vatRateBps > 0
                                            ? t(
                                                  'operations.editor.vatFraction',
                                                  {
                                                      rate: vatRateBps / 100,
                                                      gross:
                                                          100 +
                                                          vatRateBps / 100,
                                                  },
                                              )
                                            : t('operations.editor.vatNoRate')}
                                    </span>
                                ) : null}

                                <span
                                    className="ms-auto text-xs text-muted"
                                    {...getNodeTestIdProp(
                                        `operation-editor-pair-note-${pair.pairGroup}`,
                                    )}
                                >
                                    {isVat
                                        ? t('operations.editor.vatRateNote')
                                        : duplicates.length > 0
                                          ? t('operations.editor.slotAlsoIn', {
                                                pairs: duplicates.join(', '),
                                            })
                                          : ''}
                                </span>
                            </div>
                        </div>
                    );
                })}
            </Widget.Content>
            <Widget.Footer>
                {isReadOnly ? null : (
                    <Button
                        onPress={onAddPair}
                        variant="tertiary"
                        {...getNodeTestIdProp('operation-editor-add-pair')}
                    >
                        {t('operations.editor.addPair')}
                    </Button>
                )}
            </Widget.Footer>
        </Widget>
    );
};
